import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Card, CardContent, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Chip, Alert } from '@mui/material';
import { Add, Delete, ArrowBack, PersonAdd } from '@mui/icons-material';
import api from '../../services/api';

const formatLabels = {
  praesenz: { label: 'Präsenz', color: 'success' },
  online: { label: 'Online', color: 'info' },
  hybrid: { label: 'Hybrid', color: 'warning' },
};

const statusLabels = {
  geplant: { label: 'Geplant', color: 'default' },
  bestaetigt: { label: 'Bestätigt', color: 'info' },
  durchgefuehrt: { label: 'Durchgeführt', color: 'success' },
  abgesagt: { label: 'Abgesagt', color: 'error' },
};

const zielgruppeLabels = {
  multiplikator: { label: 'Multiplikator', color: '#1565C0' },
  jugendlich: { label: 'Jugendlich', color: '#F57C00' },
  erwachsener: { label: 'Erwachsener', color: '#2E7D32' },
  sonstige: { label: 'Sonstige', color: '#666' },
};

const anmeldestatusLabels = {
  angemeldet: { label: 'Angemeldet', color: 'default' },
  bestaetigt: { label: 'Bestätigt', color: 'info' },
  teilgenommen: { label: 'Teilgenommen', color: 'success' },
  abgesagt: { label: 'Abgesagt', color: 'error' },
};

const anmeldestatusCycle = ['angemeldet', 'bestaetigt', 'teilgenommen', 'abgesagt'];

const VeranstaltungDetailPage = () => {
  const { projektId, veranstaltungId } = useParams();
  const navigate = useNavigate();
  const [veranstaltung, setVeranstaltung] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    vorname: '', nachname: '', email: '', organisation: '', zielgruppe: 'sonstige'
  });

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/veranstaltungen/${veranstaltungId}`);
      setVeranstaltung(res.data);
    } catch (err) { setError('Fehler beim Laden'); }
  }, [veranstaltungId]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/veranstaltungen/${veranstaltungId}`, { status: newStatus });
      load();
    } catch (err) { setError('Fehler beim Aktualisieren'); }
  };

  const handleAddTeilnehmer = async () => {
    try {
      const data = { ...form };
      if (!data.email) delete data.email;
      if (!data.organisation) delete data.organisation;
      await api.post(`/veranstaltungen/${veranstaltungId}/teilnehmer`, data);
      setOpen(false);
      setForm({ vorname: '', nachname: '', email: '', organisation: '', zielgruppe: 'sonstige' });
      load();
    } catch (err) { setError('Fehler beim Hinzufügen'); }
  };

  const handleToggleAnmeldestatus = async (teilnehmer) => {
    const currentIdx = anmeldestatusCycle.indexOf(teilnehmer.anmeldestatus);
    const nextStatus = anmeldestatusCycle[(currentIdx + 1) % anmeldestatusCycle.length];
    try {
      await api.put(`/veranstaltungen/teilnehmer/${teilnehmer.id}`, { anmeldestatus: nextStatus });
      load();
    } catch (err) { setError('Fehler beim Aktualisieren'); }
  };

  const handleDeleteTeilnehmer = async (id) => {
    if (window.confirm('Teilnehmer wirklich entfernen?')) {
      try {
        await api.delete(`/veranstaltungen/teilnehmer/${id}`);
        load();
      } catch (err) { setError('Fehler beim Löschen'); }
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '-';
  const formatTime = (t) => t ? t.substring(0, 5) : '';

  if (!veranstaltung) return <Box sx={{ p: 3 }}><Typography>Laden...</Typography></Box>;

  const teilnehmer = veranstaltung.teilnehmer || [];
  const angemeldet = teilnehmer.filter(t => t.anmeldestatus !== 'abgesagt').length;
  const teilgenommen = teilnehmer.filter(t => t.anmeldestatus === 'teilgenommen').length;
  const abgesagt = teilnehmer.filter(t => t.anmeldestatus === 'abgesagt').length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(`/veranstaltungen/${projektId}`)} sx={{ color: '#1B5E20' }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight="bold" color="#1B5E20">{veranstaltung.titel}</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Info-Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">Datum</Typography>
              <Typography fontWeight="bold">{formatDate(veranstaltung.datum)}</Typography>
              {(veranstaltung.uhrzeitVon || veranstaltung.uhrzeitBis) && (
                <Typography variant="body2" color="text.secondary">
                  {formatTime(veranstaltung.uhrzeitVon)}{veranstaltung.uhrzeitBis ? ` – ${formatTime(veranstaltung.uhrzeitBis)}` : ''} Uhr
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">Ort</Typography>
              <Typography fontWeight="bold">{veranstaltung.ort || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">Format</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip label={formatLabels[veranstaltung.format]?.label || veranstaltung.format}
                  color={formatLabels[veranstaltung.format]?.color || 'default'} size="small" />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box sx={{ mt: 0.5 }}>
                <TextField select size="small" value={veranstaltung.status}
                  onChange={(e) => handleStatusChange(e.target.value)} variant="outlined"
                  sx={{ minWidth: 160 }}>
                  {Object.entries(statusLabels).map(([key, val]) =>
                    <MenuItem key={key} value={key}>{val.label}</MenuItem>
                  )}
                </TextField>
              </Box>
            </Grid>
          </Grid>
          {veranstaltung.beschreibung && (
            <Typography color="text.secondary" sx={{ mt: 2 }}>{veranstaltung.beschreibung}</Typography>
          )}
        </CardContent>
      </Card>

      {/* Statistik + Teilnehmerliste */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Typography variant="h5" fontWeight="bold" color="#1B5E20">Teilnehmer</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip label={`Angemeldet: ${angemeldet}`} size="small" sx={{ bgcolor: '#E8F5E9', color: '#1B5E20' }} />
            <Chip label={`Teilgenommen: ${teilgenommen}`} size="small" color="success" />
            <Chip label={`Abgesagt: ${abgesagt}`} size="small" color="error" variant="outlined" />
          </Box>
        </Box>
        <Button variant="outlined" startIcon={<PersonAdd />} onClick={() => setOpen(true)}
          sx={{ color: '#1B5E20', borderColor: '#1B5E20', '&:hover': { borderColor: '#2E7D32', bgcolor: '#E8F5E9' } }}>
          Teilnehmer hinzufügen
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#E8F5E9' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Vorname</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nachname</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>E-Mail</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Organisation</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Zielgruppe</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teilnehmer.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#999' }}>Noch keine Teilnehmer eingetragen</TableCell></TableRow>
            ) : teilnehmer.map((t) => (
              <TableRow key={t.id} hover>
                <TableCell>{t.vorname}</TableCell>
                <TableCell>{t.nachname}</TableCell>
                <TableCell>{t.email || '-'}</TableCell>
                <TableCell>{t.organisation || '-'}</TableCell>
                <TableCell>
                  <Chip label={zielgruppeLabels[t.zielgruppe]?.label || t.zielgruppe} size="small"
                    sx={{ bgcolor: zielgruppeLabels[t.zielgruppe]?.color || '#666', color: 'white', fontWeight: 'bold' }} />
                </TableCell>
                <TableCell>
                  <Chip label={anmeldestatusLabels[t.anmeldestatus]?.label || t.anmeldestatus}
                    color={anmeldestatusLabels[t.anmeldestatus]?.color || 'default'} size="small"
                    onClick={() => handleToggleAnmeldestatus(t)}
                    sx={{ cursor: 'pointer' }} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="error" onClick={() => handleDeleteTeilnehmer(t.id)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog: Teilnehmer hinzufügen */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Teilnehmer hinzufügen</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Vorname *" value={form.vorname}
            onChange={(e) => setForm({...form, vorname: e.target.value})} margin="normal" />
          <TextField fullWidth label="Nachname *" value={form.nachname}
            onChange={(e) => setForm({...form, nachname: e.target.value})} margin="normal" />
          <TextField fullWidth label="E-Mail" value={form.email}
            onChange={(e) => setForm({...form, email: e.target.value})} margin="normal" />
          <TextField fullWidth label="Organisation" value={form.organisation}
            onChange={(e) => setForm({...form, organisation: e.target.value})} margin="normal" />
          <TextField fullWidth select label="Zielgruppe" value={form.zielgruppe}
            onChange={(e) => setForm({...form, zielgruppe: e.target.value})} margin="normal">
            {Object.entries(zielgruppeLabels).map(([key, val]) =>
              <MenuItem key={key} value={key}>{val.label}</MenuItem>
            )}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button onClick={handleAddTeilnehmer} variant="contained" sx={{ bgcolor: '#1B5E20' }}>Hinzufügen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VeranstaltungDetailPage;
